import React, {useEffect, useState} from 'react';
import {
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  PaletteMode,
  Snackbar,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CompoundComponent from './CompoundComponent';
import CompoundFormComponent from './CompoundFormComponent';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { defaultCompound, fetchCompounds, setSelectedCompound } from '../../store/Host/compoundsSlice';
import { Compound } from '../../services/restserver-openapi';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { Main } from '../../layouts';
import Container from '../Container';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {getReadableCompoundType} from "../../utils/typeUtils";
import Paper from "@mui/material/Paper";

function CompoundManagementComponent() {

  const dispatch = useAppDispatch();
  const [editMode, setEditMode] = useState(false);
  const compound = useAppSelector(state => state.compounds.selectedCompound);
  const [previousCompoundBeforeCreate, setPreviousCompoundBeforeCreate] = useState<Compound | null>(null);
  const hostId = useAppSelector(state => state.login.userSpecificationId);
  const compoundBelongsToHost = Number(compound?.hostId) === Number(hostId);
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false)
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("")

  useEffect(() => {
    if (!hostId) {
      return;
    }

    dispatch(fetchCompounds({
      hostId,
      regionId: 0,
      revised: undefined,
      visible: undefined,
      types: []
    })).unwrap().then((payload) => {
      if (!payload || payload.length === 0) {
        return;
      }

      if (!compound?.compoundId || !payload.some((item: Compound) => item.compoundId === compound.compoundId)) {
        dispatch(setSelectedCompound(payload[0]));
      }
    }).catch(() => {
      // Error state is handled by slice/UI elsewhere.
    });
  }, [dispatch, hostId, compound?.compoundId]);

  const handleEditCompound = () => {
    setErrorSnackbarMessage("Vergiss nicht zu speichern!");
    setOpenErrorSnackbar(true);
    setEditMode(true);
  };

  const handleAddCompound = () => {
    setErrorSnackbarMessage("Du erfasst jetzt einen neuen Betrieb.");
    setOpenErrorSnackbar(true);
    setPreviousCompoundBeforeCreate(compound.compoundId ? compound : null);
    dispatch(setSelectedCompound({ ...defaultCompound, hostId: hostId }));
    setEditMode(true);
  };

  const onBack = async (compoundId = 0) => {
    if (compoundId === 0 && previousCompoundBeforeCreate) {
      dispatch(setSelectedCompound(previousCompoundBeforeCreate));
      setPreviousCompoundBeforeCreate(null);
      setEditMode(false);
      return;
    }

    setEditMode(false);
    setPreviousCompoundBeforeCreate(null);

    if (hostId) {
      await dispatch(fetchCompounds({
        hostId,
        regionId: 0,
        revised: undefined,
        visible: undefined,
        types: []
      })).unwrap().then((payload) => {
        if (payload) {
          if (payload.length > 0) {
            dispatch(setSelectedCompound(payload.find((item: Compound) => item.compoundId === compoundId) || payload[0]));
          } else {
            dispatch(setSelectedCompound({ ...defaultCompound, hostId }));
          }
        }
      });
    }
  };

  const hasExistingCompound = compound.compoundId !== 0;

  const HeaderContent = () => (
    <Box data-aos={isMd ? 'fade-right' : 'fade-up'}>
      <Typography
        sx={{
          textTransform: 'uppercase',
          fontWeight: 500,
          letterSpacing: 1,
        }}
        gutterBottom
        color={'text.secondary'}
      >
        Gastgeber
      </Typography>
      <Box marginBottom={2}>
        <Typography
          variant="h3"
          color="text.primary"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Hier pflegst Du{' '}
          <Typography
            color={'text.primary'}
            component={'span'}
            variant={'inherit'}
            sx={{
              background: `linear-gradient(180deg, transparent 82%, ${alpha(
                mode === 'light' as PaletteMode ? theme.palette.secondary.main : theme.palette.primary.main,
                0.3,
              )} 0%)`,
            }}
          >
            {hasExistingCompound ? compound.name : 'Deinen ersten Betrieb'}
          </Typography>
        </Typography>
      </Box>
      <Box marginBottom={3}>
        <Typography variant="h6" component="p" color="text.secondary">
          {hasExistingCompound
            ? 'Hier pflegst Du alle Daten zu Deinem Betrieb. Wenn Du einen anderen Betrieb bearbeiten möchtest, dann verwende den Wechsler oben rechts neben deinem Profilbild. Unterkünfte erfasst und bearbeitest Du unter Verfügbarkeiten.'
            : 'Lege Deinen ersten Betrieb an und erfasse danach alle Details wie Kontakt, Ausstattung und Unterkünfte.'}
        </Typography>
      </Box>
    </Box>
  );

  const ActionRow = (props: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    active?: boolean;
    disabled?: boolean;
    tooltip?: string;
    onClick?: () => void;
  }) => {
    const { icon, title, subtitle, active, disabled, tooltip, onClick } = props;

    const content = (
      <ListItemButton
        disabled={disabled}
        onClick={onClick}
        sx={{
          borderRadius: 2,
          px: 1.25,
          py: 1,
          gap: 0.5,
          '&.Mui-disabled': { opacity: 0.55 },
          ...(active
            ? {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              }
            : {}),
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
          {icon}
        </ListItemIcon>

        <ListItemText
          primary={<Typography sx={{ fontWeight: 800 }} noWrap>{title}</Typography>}
          secondary={subtitle}
          secondaryTypographyProps={{ color: 'text.secondary' }}
        />
      </ListItemButton>
    );

    return tooltip ? (
      <Tooltip title={tooltip} arrow>
        <Box component="span">{content}</Box>
      </Tooltip>
    ) : (
      content
    );
  };


  return (
    <Main>
      <Snackbar open={openErrorSnackbar} autoHideDuration={6000}
                onClose={() => setOpenErrorSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenErrorSnackbar(false)} severity="info">
          {errorSnackbarMessage}
        </Alert>
      </Snackbar>
      <Container>
        <HeaderContent />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 4,
              border: 0,
              boxShadow: 'none',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Aktionen
            </Typography>
            <List disablePadding>
              {!editMode ? (
                <>
                  <ActionRow
                    icon={<EditIcon />}
                    title="Betrieb bearbeiten"
                    subtitle="Daten und Einstellungen anpassen"
                    disabled={!hasExistingCompound || !compoundBelongsToHost}
                    tooltip={!hasExistingCompound
                      ? 'Du kannst nach dem Erstellen Deines ersten Betriebs bearbeiten.'
                      : !compoundBelongsToHost
                        ? "Du kannst den Betrieb vom Typ " + getReadableCompoundType(compound) + " nicht bearbeiten, da er nicht dir gehört"
                        : undefined}
                    onClick={handleEditCompound}
                  />

                  <ActionRow
                    icon={<DeleteIcon />}
                    title="Betrieb löschen"
                    subtitle="Der Lösch-Workflow ist derzeit nicht verfügbar"
                    disabled
                    tooltip="Betrieb löschen ist aktuell deaktiviert."
                  />

                  <ActionRow
                    icon={<AddCircleIcon />}
                    title="Neuen Betrieb erstellen"
                    subtitle="Weiteren Betrieb hinzufügen"
                    onClick={handleAddCompound}
                  />
                </>
              ) : (
                <ActionRow
                  icon={<CancelIcon />}
                  title="Bearbeitung beenden"
                  subtitle="Zur Detailansicht zurück"
                  active
                  onClick={() => onBack(compound.compoundId)}
                />
              )}
            </List>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              p: { xs: 2, md: 3 },
              border: 0,
              boxShadow: 'none',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="overline" color="text.secondary">
              Betriebsdaten
            </Typography>
            {editMode ? (
              <>
                <Typography variant="h6" component="h2" gutterBottom>
                  {hasExistingCompound ? 'Betrieb bearbeiten' : 'Neuen Betrieb erstellen'}
                </Typography>
                <CompoundFormComponent onBack={onBack} />
              </>
            ) : hasExistingCompound ? (
              <>
                <Typography variant="h6" component="h2" gutterBottom>
                  Erfasste Daten
                </Typography>
                <CompoundComponent />
              </>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                Du hast noch keinen Betrieb erstellt. Nutze links "Neuen Betrieb erstellen", um zu starten.
              </Alert>
            )}
          </Paper>
        </Box>
      </Container>

    </Main>
  );
}

export default CompoundManagementComponent;
